import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Package, LayoutGrid, List, Search, X,
  Carrot, Droplets, Flame, Fish, Cake, Archive,
  Snowflake, Cookie, Coffee, HeartPulse, User,
  ShoppingBag, Star, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { Card, Button } from '../UI';
import { useFavoriteProducts } from '../../hooks/useFavoriteProducts';
import ProductDetailView from '../Products/ProductDetailView';
import AddToListDropdown from '../Products/AddToListDropdown';

// ─────────────────────────────────────────────────────────────
// Category config — mirrors iOS + ProductCard
// ─────────────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
  produce:   { icon: Carrot,     gradient: 'from-[#E8F4F1] to-[#C9E8E0]', color: 'text-primary'   },
  dairy:     { icon: Droplets,   gradient: 'from-[#FEF3E2] to-[#FDEBD0]', color: 'text-accent'    },
  meat:      { icon: Flame,      gradient: 'from-[#F0F4FF] to-[#E0E8FF]', color: 'text-[#5B7BCC]' },
  seafood:   { icon: Fish,       gradient: 'from-[#E8F4F1] to-[#D4EDE8]', color: 'text-primary'   },
  bakery:    { icon: Cake,       gradient: 'from-[#FEF3E2] to-[#FDEBD0]', color: 'text-accent'    },
  pantry:    { icon: Archive,    gradient: 'from-[#FEF3E2] to-[#FDEBD0]', color: 'text-accent'    },
  frozen:    { icon: Snowflake,  gradient: 'from-[#F0F4FF] to-[#E0E8FF]', color: 'text-[#5B7BCC]' },
  snacks:    { icon: Cookie,     gradient: 'from-[#E8F4F1] to-[#D4EDE8]', color: 'text-primary'   },
  beverages: { icon: Coffee,     gradient: 'from-[#E8F4F1] to-[#D4EDE8]', color: 'text-primary'   },
  health:    { icon: HeartPulse, gradient: 'from-[#E8F4F1] to-[#C9E8E0]', color: 'text-primary'   },
  personal:  { icon: User,       gradient: 'from-[#E8F4F1] to-[#D4EDE8]', color: 'text-primary'   },
};
const DEFAULT_CAT = { icon: Package, gradient: 'from-[#E8F4F1] to-[#D4EDE8]', color: 'text-primary' };
const getCatConfig = (cat) => CATEGORY_CONFIG[(cat || '').toLowerCase()] || DEFAULT_CAT;

// ─────────────────────────────────────────────────────────────
// Image resolution — mirrors iOS primaryImageURL
// ─────────────────────────────────────────────────────────────
const getPrimaryImageURL = (product) => {
  const images = product.images;
  if (Array.isArray(images) && images.length > 0) {
    const primary = images.find(img => img.isPrimary) || images[0];
    return primary?.urls?.medium || primary?.urls?.full || primary?.urls?.thumb || null;
  }
  return product.imageURL || product.imageUrl || product.image || null;
};

// ─────────────────────────────────────────────────────────────
// Allergen count — mirrors iOS allergenWarnings
// ─────────────────────────────────────────────────────────────
const getAllergenCount = (product) => {
  const fromProduct = Array.isArray(product.allergens) ? product.allergens : [];
  const fromIngredients = Array.isArray(product.ingredients)
    ? product.ingredients.flatMap(i => Array.isArray(i.allergens) ? i.allergens : [])
    : [];
  return new Set([...fromProduct, ...fromIngredients]).size;
};

// ─────────────────────────────────────────────────────────────
// FavoriteProductCard — compact card for the favorites grid
// ─────────────────────────────────────────────────────────────
const FavoriteProductCard = ({ product, onTap, onUnfavorite, viewMode }) => {
  const catConfig     = getCatConfig(product.category);
  const CatIcon       = catConfig.icon;
  const imageUrl      = getPrimaryImageURL(product);
  const allergenCount = getAllergenCount(product);

  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-md px-md py-sm bg-white border border-text-tertiary/10 rounded-2xl hover:border-primary/25 hover:shadow-md transition-all group">

        {/* Thumbnail */}
        <div
          onClick={onTap}
          className={`w-14 h-14 flex-shrink-0 bg-gradient-to-br ${catConfig.gradient} rounded-xl overflow-hidden flex items-center justify-center cursor-pointer`}
        >
          {imageUrl ? (
            <img src={imageUrl} alt={product.name} className="w-full h-full object-contain p-1"
              onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <CatIcon size={22} className={`${catConfig.color} opacity-40`} strokeWidth={1.2} />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onTap}>
          {product.brand && (
            <p className="text-[11px] font-bold text-primary uppercase tracking-wider mb-0.5 truncate">
              {product.brand}
            </p>
          )}
          <p className="text-sm font-semibold text-text-primary truncate">{product.name}</p>
          <div className="flex items-center gap-md mt-0.5">
            {product.rating > 0 && (
              <div className="flex items-center gap-xs">
                <Star size={9} className="fill-[#F5A623] text-[#F5A623]" />
                <span className="text-[10px] text-text-secondary font-semibold">
                  {product.rating.toFixed(1)}
                </span>
              </div>
            )}
            {allergenCount > 0 && (
              <div className="flex items-center gap-xs">
                <AlertTriangle size={9} className="text-orange-500" />
                <span className="text-[10px] text-orange-500 font-medium">
                  {allergenCount} allergen{allergenCount !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-sm flex-shrink-0" onClick={e => e.stopPropagation()}>
          <div className="w-28">
            <AddToListDropdown product={product} onSuccess={() => {}} />
          </div>
          <button
            onClick={onUnfavorite}
            className="w-8 h-8 rounded-full bg-error/10 hover:bg-error/20 flex items-center justify-center transition-all"
            title="Remove from favorites"
          >
            <Heart size={14} className="fill-error text-error" />
          </button>
        </div>
      </div>
    );
  }

  // Grid card
  return (
    <div className="bg-white rounded-2xl overflow-hidden flex flex-col border border-text-tertiary/10 hover:border-primary/25 hover:shadow-xl transition-all duration-200 shadow-sm group">

      {/* Image */}
      <div
        onClick={onTap}
        className={`relative bg-gradient-to-br ${catConfig.gradient} cursor-pointer`}
        style={{ height: '160px' }}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={product.name}
            className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CatIcon size={40} className={`${catConfig.color} opacity-25`} strokeWidth={1} />
          </div>
        )}

        {/* Unfavorite button */}
        <button
          onClick={e => { e.stopPropagation(); onUnfavorite(); }}
          className="absolute top-2 left-2 w-8 h-8 rounded-full bg-error/90 hover:bg-error flex items-center justify-center shadow-md transition-all hover:scale-110"
          title="Remove from favorites"
        >
          <Heart size={14} className="fill-white text-white" />
        </button>

        {/* Verified */}
        {product.isVerified && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-success flex items-center justify-center shadow-sm">
            <ShieldCheck size={12} className="text-white" />
          </div>
        )}

        {/* Allergen badge */}
        {allergenCount > 0 && (
          <div className="absolute bottom-2 left-2 flex items-center gap-xs px-2 py-1 bg-orange-500 rounded-full">
            <AlertTriangle size={8} className="text-white" strokeWidth={2.5} />
            <span className="text-[9px] font-bold text-white">{allergenCount}</span>
          </div>
        )}

        {/* First dietary tag */}
        {(product.dietaryTags || []).length > 0 && (
          <div className="absolute bottom-2 right-2">
            <span className={`text-[9px] font-semibold px-2 py-0.5 bg-white/95 rounded-full ${catConfig.color} shadow-sm`}>
              {product.dietaryTags[0]}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div onClick={onTap} className="cursor-pointer flex-1" style={{ padding: '14px 14px 10px' }}>
        {product.brand && (
          <p className="text-[11px] font-bold text-primary uppercase tracking-wider mb-1 truncate">
            {product.brand}
          </p>
        )}
        <h3 className="text-sm font-semibold text-text-primary leading-snug line-clamp-2 mb-2"
          style={{ minHeight: '2.5rem' }}>
          {product.name}
        </h3>
        {product.rating > 0 && (
          <div className="flex items-center gap-xs mb-1">
            {[1,2,3,4,5].map(i => (
              <Star key={i} size={9}
                className={i <= Math.round(product.rating) ? 'fill-[#F5A623] text-[#F5A623]' : 'text-text-tertiary/30'} />
            ))}
            <span className="text-[10px] text-text-secondary font-semibold ml-0.5">
              {product.rating.toFixed(1)}
            </span>
          </div>
        )}
      </div>

      {/* Add to list */}
      <div style={{ padding: '10px 14px 14px', borderTop: '1px solid rgba(0,0,0,0.06)' }}
        onClick={e => e.stopPropagation()}>
        <AddToListDropdown product={product} onSuccess={() => {}} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// FavoriteProductsView
// ─────────────────────────────────────────────────────────────
const FavoriteProductsView = () => {
  const navigate = useNavigate();
  const { favoriteProducts, loading, toggleFavorite } = useFavoriteProducts();

  const [viewMode,         setViewMode]         = useState('grid');
  const [searchQuery,      setSearchQuery]       = useState('');
  const [selectedProduct,  setSelectedProduct]   = useState(null);

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return favoriteProducts;
    const q = searchQuery.toLowerCase();
    return favoriteProducts.filter(p =>
      (p.name  || '').toLowerCase().includes(q) ||
      (p.brand || '').toLowerCase().includes(q)
    );
  }, [favoriteProducts, searchQuery]);

  // ── Category breakdown for stats ─────────────────────────────────────────
  const categoryCount = useMemo(() =>
    new Set(favoriteProducts.map(p => p.category).filter(Boolean)).size,
    [favoriteProducts]
  );

  const allergenProducts = useMemo(() =>
    favoriteProducts.filter(p => getAllergenCount(p) > 0).length,
    [favoriteProducts]
  );

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-xxxl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-md" />
        <p className="text-callout text-text-secondary">Loading favorites...</p>
      </div>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  if (favoriteProducts.length === 0) {
    return (
      <div className="space-y-xl">
        <Card className="border-2 border-error/20">
          <div className="flex items-center gap-md">
            <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
              <Heart size={32} className="text-error" />
            </div>
            <div>
              <h2 className="text-title-2 text-primary mb-xs">Saved Products</h2>
              <p className="text-body text-text-secondary">Your favorite products in one place</p>
            </div>
          </div>
        </Card>

        <Card className="text-center py-xxxl">
          <div className="w-20 h-20 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-lg">
            <Package size={40} className="text-error/60" />
          </div>
          <h3 className="text-title-3 text-primary mb-sm">No Saved Products Yet</h3>
          <p className="text-body text-text-secondary mb-lg max-w-sm mx-auto">
            Tap the heart icon on any product to save it here for quick access
          </p>
          <Button variant="primary" onClick={() => navigate('/products')}>
            Browse Products
          </Button>
        </Card>
      </div>
    );
  }

  // ── Main view ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-lg">

      {/* Header */}
      <Card className="border-2 border-error/20">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-md">
            <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
              <Heart size={28} className="text-error fill-error" />
            </div>
            <div>
              <h2 className="text-title-2 text-primary mb-xs">Saved Products</h2>
              <p className="text-body text-text-secondary">
                {favoriteProducts.length} product{favoriteProducts.length !== 1 ? 's' : ''} saved
              </p>
            </div>
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-xs p-1 bg-surface rounded-xl border border-text-tertiary/10">
            <button
              onClick={() => setViewMode('grid')}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                viewMode === 'grid' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:text-primary'
              }`}
            >
              <LayoutGrid size={15} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                viewMode === 'list' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:text-primary'
              }`}
            >
              <List size={15} />
            </button>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-md">
        <Card>
          <p className="text-caption text-text-secondary mb-xs">Total Saved</p>
          <p className="text-title-3 font-bold text-primary">{favoriteProducts.length}</p>
        </Card>
        <Card>
          <p className="text-caption text-text-secondary mb-xs">Categories</p>
          <p className="text-title-3 font-bold text-accent">{categoryCount}</p>
        </Card>
        <Card>
          <p className="text-caption text-text-secondary mb-xs">With Allergens</p>
          <p className="text-title-3 font-bold text-orange-500">{allergenProducts}</p>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-sm px-md py-sm bg-white border border-text-tertiary/15 rounded-xl focus-within:border-primary transition-all">
        <Search size={15} className="text-text-tertiary flex-shrink-0" />
        <input
          type="text"
          placeholder="Search saved products..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')}>
            <X size={13} className="text-text-tertiary hover:text-text-primary transition-colors" />
          </button>
        )}
      </div>

      {/* Results count when searching */}
      {searchQuery && (
        <p className="text-caption text-text-tertiary">
          {filtered.length} of {favoriteProducts.length} products
        </p>
      )}

      {/* Empty search state */}
      {filtered.length === 0 && searchQuery && (
        <Card className="text-center py-xl">
          <Package size={32} className="text-text-tertiary/40 mx-auto mb-sm" />
          <p className="text-callout text-text-secondary">No products match "{searchQuery}"</p>
          <button
            onClick={() => setSearchQuery('')}
            className="text-caption text-primary hover:underline mt-xs"
          >
            Clear search
          </button>
        </Card>
      )}

      {/* Product grid / list */}
      {filtered.length > 0 && (
        viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
            {filtered.map(product => (
              <FavoriteProductCard
                key={product.id}
                product={product}
                viewMode="grid"
                onTap={() => setSelectedProduct(product)}
                onUnfavorite={() => toggleFavorite(product)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filtered.map(product => (
              <FavoriteProductCard
                key={product.id}
                product={product}
                viewMode="list"
                onTap={() => setSelectedProduct(product)}
                onUnfavorite={() => toggleFavorite(product)}
              />
            ))}
          </div>
        )
      )}

      {/* Browse more */}
      <Card>
        <h3 className="text-callout font-semibold text-text-primary mb-md">Discover More</h3>
        <button
          onClick={() => navigate('/products')}
          className="w-full flex items-center gap-md p-md rounded-xl hover:bg-primary/5 transition-all text-left group"
        >
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-all">
            <ShoppingBag size={18} className="text-primary group-hover:text-white transition-all" />
          </div>
          <div>
            <p className="text-callout font-semibold text-text-primary">Browse Products</p>
            <p className="text-caption text-text-secondary">Find more products to save</p>
          </div>
        </button>
      </Card>

      {/* Product detail modal */}
      {selectedProduct && (
        <ProductDetailView
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};

export default FavoriteProductsView;
